import { batch } from 'react-redux';

import { DataLakeEnums, DataLakeQuery, DataLakeReadableConsts, FileField } from 'dashboard-services/api/dataLake';
import { ActionService, CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { globalActions, loginActions, statusBoxActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import { inspectorActions, ncsvCreatorActions } from 'actions/modules/tools';
import { routing } from 'routing';

import { commonDatalakeActions } from './';

import dayjs from 'dayjs';

import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';

const wildcardQuery = query => `*${query}*`
export default (() => {
  const UUID_PATTERN = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
  const UUID_PREFIXED_REGEXP = new RegExp(`^uuid=${UUID_PATTERN}$`);
  const UUID_REGEXP = new RegExp(`^${UUID_PATTERN}$`);
  const DATA_START_METADATA = "dataStart"
  const DATA_END_METADATA = "dataEnd"
  const PRIORITY_METADATA = "priority"
  const FORWARD_CURVE_METADATA = "Forward_Curve"

  const isUuidQuery = query => UUID_REGEXP.test(query)
  const isPrefixedUuidQuery = query => UUID_PREFIXED_REGEXP.test(query) 

  const getQuery = ({ query = "", fileType, from, to, groupName, isDraft, draftStatus, reviewStatus, tags, reviewer, latest }) => (_, getState) => {
    const datalakeLayout = getState().myAccountState.settingsState.datalakeLayout || commonDatalakeActions.DATALAKE_LAYOUT.VERSIONS,
          isLatest = latest ?? (datalakeLayout !== commonDatalakeActions.DATALAKE_LAYOUT.VERSIONS),
          hasApprovalFilters = isDraft !== undefined || draftStatus || reviewStatus || tags?.length > 0 || reviewer
    
    if(isUuidQuery(query)){
      query = `uuid=${query}`
      if(!hasApprovalFilters) {
        return query
      }
    }

    const localQuery = []
    fileType && localQuery.push(`type=${fileType}`);
    from && localQuery.push(`arrivalTime>${dayjs.utc(from).valueOf()}`)
    to && localQuery.push(`arrivalTime<${dayjs.utc(to).valueOf()}`)
    isLatest && (hasApprovalFilters || !isPrefixedUuidQuery(query)) && localQuery.push(`latest=true`)
    groupName && localQuery.push(`groupName=${groupName}`);
    isDraft !== undefined && localQuery.push(`isDraft=${isDraft}`)
    draftStatus && localQuery.push(`draftStatus=${draftStatus}`)
    reviewStatus && localQuery.push(`review.status=${reviewStatus}`)
    tags?.filter(Boolean).forEach(tag => localQuery.push(`fields.tags=*${tag}*`))
    reviewer && localQuery.push(`reviewer=${reviewer}`)
    const generatedLocalQuery = localQuery.join("&");
    
    const checkedQuery = String(query).includes("=") ? query : wildcardQuery(query); 
    if(query !== "" && generatedLocalQuery) {
      return `${generatedLocalQuery}&${checkedQuery}`
    } else if(query !== "") {
      return checkedQuery;
    } else if (!generatedLocalQuery) {
      return "*"
    }
    return generatedLocalQuery;
  }

  const getApprovalsQuery = filters => dispatch => dispatch(getQuery({
    ...filters,
    latest: true,
    isDraft: true,
    draftStatus: DataLakeEnums.DRAFT_STATUS.IN_REVIEW
  }))

  const getWithStatus = () => (dispatch, getState) => {
    const groupName = getState().datalakeState.filesState.group?.name
    const hasStatusAccess = dispatch(loginActions.hasAccessTo(routing.statuses.name))
    return groupName !== undefined && hasStatusAccess
  }

  const openSystemFilePicker = ({
    groupName, 
    fileName,
    fileType,
    openDialog = true,
    multiple = true
  } = {}) => dispatch => {
    const input = document.createElement('input')
    input.onchange = e => dispatch(onFilePicked({
      e, 
      groupName, 
      openDialog,
      fileName,
      fileType
    }))
    input.setAttribute('type', 'file');
    multiple && input.setAttribute('multiple', 'true'); 
    input.style.display = 'none';
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  }

  const onFilePicked = ({ e, groupName, fileName, fileType, openDialog = true }) => (dispatch, getState) => {
    batch(() => {
      dispatch(setFileToUpload(e))
      if(openDialog) {
        dispatch(onChange(true, "isOpen"))
        if (groupName === undefined) {
          const localGroupName = getState().myAccountState.settingsState.defaultGroup
          dispatch(onChange(localGroupName, "groupName"))  
        } else {
          dispatch(onChange(groupName, "groupName"))
        }
        fileType && dispatch(onChange(DataLakeReadableConsts.FILE_TYPES.find(dl => dl.value === fileType), "type"))
        fileName && dispatch(onChange([fileName], "uploadedFilesName"))
      }
    })
  }

  const ON_CHANGE = 'ON_CHANGE_DL_FILES'
  const onChange = ActionService.makeActionCreator(ON_CHANGE, 'value', 'name')

  const ADD_UPLOADED_FILE = 'ADD_UPLOADED_FILE_DL_FILES'
  const addUploadedFile = ActionService.makeActionCreator(ADD_UPLOADED_FILE, 'item')

  const REMOVE_UPLOADED_FILE = 'REMOVE_UPLOADED_FILE_DL_FILES'
  const removeUploadedFile = ActionService.makeActionCreator(REMOVE_UPLOADED_FILE, 'index')

  const CHANGE_UPLOADED_FILE_NAME = 'CHANGE_UPLOADED_FILE_NAME_DL_FILES'
  const changeUploadedFileName = ActionService.makeActionCreator(CHANGE_UPLOADED_FILE_NAME, 'value', 'index')
  
  const CLEAR_UPLOAD_DIALOG = 'CLEAR_UPLOAD_DIALOG_DL_FILES'
  const clearUploadDialog = ActionService.makeActionCreator(CLEAR_UPLOAD_DIALOG)

  const setFileToUpload = e => dispatch => {
    const files = e.dataTransfer?.files || e.target?.files || []
    batch(() => Array.from(files).forEach(file => dispatch(addUploadedFile(file))))
  }

  const SET_ERRORS = 'SET_ERRORS_DL_FILES'
  const setErrors = ActionService.makeActionCreator(SET_ERRORS, 'errors')

  const ADD_FIELD = 'ADD_FIELD_DL_FILES'
  const addField = ActionService.makeActionCreator(ADD_FIELD)

  const REMOVE_FIELD = 'REMOVE_FIELD_DL_FILES'
  const removeField = ActionService.makeActionCreator(REMOVE_FIELD, 'index')

  const CHANGE_FIELD = 'CHANGE_FIELD_DL_FILES'
  const changeField = ActionService.makeActionCreator(CHANGE_FIELD, 'value', 'name', 'index')

  const SET_FIELDS = 'SET_FIELDS_DL_FILES'
  const setFields = ActionService.makeActionCreator(SET_FIELDS, 'fields')

  const isZip = name => name.split('.').pop() === "zip"

  const uploadFile = ({ queryClient, metadataCall, contentCall }) => (dispatch, getState) => {
    const { 
      groupName, 
      type = DataLakeReadableConsts.FILE_TYPES.find(dl => dl?.value === DataLakeEnums.FILE_TYPE.SOURCE), 
      fields, 
      partialUpdate, 
      avoidDuplicates, 
      skipMetadata, 
      uploadedFiles, 
      uploadedFilesName,
      dataStartDate,
      dataStartTime = "00:00",
      dataEndDate,
      dataEndTime = "23:59",
      withDataRange = false
    } = getState().datalakeState.filesState,
    definedTime = getState().myAccountState.settingsState.definedTime
    
    let parsedFields = fields.map(({ name, value }) => new FileField(name, value).serialize())
    
    parsedFields.push(new FileField("PARTIAL_UPDATE", String(partialUpdate)).serialize())
    parsedFields.push(new FileField("AVOID_DUPLICATES", String(avoidDuplicates)).serialize())
    parsedFields.push(new FileField("SKIP_METADATA", String(skipMetadata)).serialize())
    if(withDataRange) {
      parsedFields = parsedFields.filter(({ name }) => name !== DATA_START_METADATA && name !== DATA_END_METADATA)
      parsedFields.push(new FileField(DATA_START_METADATA, dayjs.tz(`${dataStartDate}T${dataStartTime}:00.000`, TimeService.translateFormat(TimeService.BACKEND_ISO_DATE_TIME_FORMAT), definedTime).utc().valueOf()).serialize())
      parsedFields.push(new FileField(DATA_END_METADATA, dayjs.tz(`${dataEndDate}T${dataEndTime}:59.999`, TimeService.translateFormat(TimeService.BACKEND_ISO_DATE_TIME_FORMAT), definedTime).utc().valueOf()).serialize())
    }
    return Promise.all(Array.from(uploadedFiles).map((uploadedFile, index) =>
      metadataCall({
        fileName: uploadedFilesName[index],
        groupName,
        fileType: type?.value,
        fields: parsedFields,
        zipped: isZip(uploadedFilesName[index])
      })
      .then(response => {
        const jobId = response.headers.get("X-KH-JOB-ID")
        dispatch(statusBoxActions.addJob({ jobId, displayName: uploadedFilesName[index], subtitle: groupName }))
        return response
      })
      .then(response => response.json())
      .then(response => contentCall({ url: response.location, image: uploadedFile }))
    ))
    .then(() => setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000))
    .then(() => dispatch(globalActions.getMessageStore()).info(uploadedFiles.length > 1 ? "Files are being processed." : "File is being processed."))
  }

  const getFileTypes = () => dispatch => {
    const hasAdminAccess = dispatch(loginActions.hasAccessTo(routing.admin.name)),
          hasGroupConfigAccess = dispatch(loginActions.hasAccessTo(routing.groupConfig.name)),
          defaultTypes = [
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.SOURCE),
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.NCSV),
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.D_NCSV),
            DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.MARKDOWN)
          ]
    if(hasAdminAccess || hasGroupConfigAccess) {
      defaultTypes.push(DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.CONFIG))
    }
    if(hasAdminAccess) {
      defaultTypes.push(DataLakeReadableConsts.FILE_TYPES.find(n => n.value === DataLakeEnums.FILE_TYPE.COMMAND_JSON))
    }
    return defaultTypes;
  }

  const SET_DROP_DEPTH = 'SET_DROP_DEPTH_DL_FILES'
  const setDropDepth = ActionService.makeActionCreator(SET_DROP_DEPTH, 'value')

  const openInspectUsage = ({ groupName, fileName }) => dispatch => {
    batch(() => {
      dispatch(inspectorActions.changeValue(groupName, 'groupName'))
      dispatch(inspectorActions.changeValue(fileName, 'name'))
      dispatch(inspectorActions.changeValue("File", 'title'))
      dispatch(inspectorActions.changeValue('datalake', 'type'))
      dispatch(inspectorActions.changeValue(true, 'open'))
    })
  }

  const showInStatuses = ({ jobId }) => dispatch => batch(() => {
    dispatch(statusesActions.changeValue(jobId, 'jobId'))
    dispatch(statusesActions.changeValue(statusesActions.MODES.DETAILS, 'mode'))
    dispatch(statusesActions.changeValue(true, 'open'))
  })

  const openCsvParser = ({ id }) => dispatch => batch(() => {
    dispatch(ncsvCreatorActions.changeValue(id, 'id'))
    dispatch(ncsvCreatorActions.changeValue(true, 'open'))
    dispatch(ncsvCreatorActions.changeValue(() => {
      // setTimeout(() => dispatch(refreshFiles()), 3000)
    }, 'onSave'))
  })

  const openTask = ({ id }) => CommonService.openInNewTab(DashboardLinkFactory.dataScience.task({ taskId: id }))

  const createDataPrep = ({ id, storageScreen }) => {
    const url = DashboardLinkFactory.dataStorage.newDataPrep({ sourceId: id, storageScreen })
    CommonService.openInNewTab(url)
  }

  const getFileSize = size => {
    if (size >= 0) {
      const sizeInKB = size/1000;
      if( sizeInKB < 1000) {
        return Number.parseFloat(sizeInKB).toFixed(2) + " KB" ;
      } else {
        return Number.parseFloat(sizeInKB/1000).toFixed(2) + " MB";
      }
    } else {
      return ""
    }
  }

  const deleteFileWithToast = ({ type, fileName, onDelete }) => dispatch =>
    dispatch(globalActions.getToastsStore()).info(
      type === DataLakeEnums.FILE_TYPE.NCSV ? `${fileName} and data associated with it will be deleted immediately. You can't undo this action.` : `${fileName} will be deleted immediately. You can't undo this action.`,
      "Delete file",
      {
        withCancel: false,
        confirmText: "Delete",
        confirmAction: () => onDelete()
      }
    )

  const downloadMultipleFiles = ({ files, getDefinitionCall }) => dispatch => {
    const locations = []
    let promise = Promise.resolve()
    files.forEach(file => {
      promise = promise.then(() => 
        getDefinitionCall({ id: file.fid })
          .then(response => {
            const location = response.headers.get("Location");
            locations.push(location)
          })
        )
      })
    promise.then(() => {
      let timeout = 0;
      // nasty trick to omit the block for downloading multiple files
      locations.forEach(url => {
        setTimeout(() => CommonService.openInNewTab(url, false), timeout);
        timeout += 1000;
      })
    })
    .then(() => dispatch(globalActions.getMessageStore()).success("Download started."))
  }

  const FILE_DETAILS_TABS = {
    DETAILS: "Details",
    VERSIONS: "Versions",
    METADATA: "Metadata"
  }

  const closeFileDetails = () => (dispatch, getState) => {
    const selectedFile = getState().datalakeState.filesState.selectedFile
    if(selectedFile !== undefined) {
      dispatch(onChange(undefined, "selectedFile"))
      dispatch(onChange(undefined, "selectedFileWriteAccess"))
      dispatch(onChange(undefined, "selectedFileVersion"))
      dispatch(onChange(undefined, "detailsMenuTab"))
    }
  }

  return {
    getQuery,
    getApprovalsQuery,
    getWithStatus,
    ON_CHANGE,
    openSystemFilePicker,
    onChange,
    SET_ERRORS,
    ADD_FIELD,
    REMOVE_FIELD,
    CHANGE_FIELD,
    SET_FIELDS,
    changeField,
    removeField,
    addField,
    setErrors,
    setFields,
    uploadFile,
    onFilePicked,
    SET_DROP_DEPTH,
    setDropDepth,
    ADD_UPLOADED_FILE,
    addUploadedFile,
    REMOVE_UPLOADED_FILE,
    removeUploadedFile,
    CHANGE_UPLOADED_FILE_NAME,
    changeUploadedFileName,
    getFileTypes,
    openInspectUsage,
    showInStatuses,
    openCsvParser,
    openTask,
    createDataPrep,
    getFileSize,
    deleteFileWithToast,
    downloadMultipleFiles,
    FILE_DETAILS_TABS,
    DATA_START_METADATA,
    DATA_END_METADATA,
    clearUploadDialog,
    CLEAR_UPLOAD_DIALOG,
    isUuidQuery,
    closeFileDetails,
    PRIORITY_METADATA,
    FORWARD_CURVE_METADATA
  }
})()
